/*
 * � 2020 Microsoft. All rights reserved.
 */

define('Arkadium/GameEngine/Tween/Tween',['lodash', 'pixi', 'Arkadium/GameEngine/Core/EngineManagers', 'Arkadium/GameEngine/Maths/Vector2'], function (_, PIXI, EngineManagers, Vector2) {

    var easeFloat = function (ease, t, b, e, d) {
        var c = e - b;
        return b + c*ease(t / d);
    };

    var easePoint = function (ease, t, b, e, d) {
        var r = new PIXI.Point();
        var cx = e.x - b.x,
            cy = e.y - b.y;
        r.x = b.x + cx*ease(t / d);
        r.y = b.y + cy*ease(t / d);
        return r;
    };

    var Tween = function (target, props) {
        this.initTween(target, props);
    };
    /// <summary>
    /// A list of supported tweenable properties.
    /// </summary>
    Tween.TweenableProperty =
    {
        Scale: 0,
        Scale_X: 1,
        Scale_Y: 2,
        Scale_Z: 3,
        Position: 4,
        Position_X: 5,
        Position_Y: 6,
        Position_Z: 7,
        Rotation: 8,
        Rotation_X: 9,
        Rotation_Y: 10,
        Rotation_Z: 11,
        Alpha: 12,
        Color: 13,
        Color_R: 14,
        Color_G: 15,
        Color_B: 16,
        Custom: 17
    };

    /// <summary>
    /// Properties that control a tween instance.
    /// </summary>
    Tween.TweenProperties = function () {
        this.initTweenProperties();
    };
    Tween.TweenProperties.prototype.initTweenProperties = function ()
    {
        /// <summary>
        /// Is the tween frame based or time based?
        /// </summary>
       this.FrameBased = false;

        /// <summary>
        /// How many times should the entire tween loop?
        /// -1 will loop infinitely.
        /// </summary>
        this.NumLoops = 0;
    };

    /// <summary>
    /// Class that controls the interpolation of scene node properties.
    /// Tweens are started by calling Start() with a scene node.
    /// After starting a tween, new interpolations can be "chained" together to
    /// create more complex tween sequences.
    ///
    /// Tween currently supports interpolation to a value, interpolation by a value, waiting, and the calling of simple delegate functions.
    /// <example>
    /// An example of a simple tween:
    /// <code>
    /// Tween.Start(target).MoveTo(new Vector3(0,100,0), 1);
    /// </code>
    /// This moves a node to (0,100,0) over 1 second.
    /// </example>
    /// <example>
    /// More complicated tweens can involve chaining.
    /// <code>
    /// Tween.Start(target).MoveTo(new Vector3(0,100,0), 3).Wait(5).ScaleBy(new Vector3(1,1,1), 2, Bounce.EaseOut).Wait(2).Call(onTweenComplete);
    /// </code>
    /// This moves a node to (0,100,0) over 3 seconds, waits 5 seconds, scales the object by (1,1,1) over 2 seconds using the Bounce ease, waits 2 seconds, then calls the OnTweenComplete delegate.
    /// </example>
    /// <example>
    /// Properties can also be tweened using the To() or By() syntax along with the property type.
    /// <code>
    /// Tween.Start(target).To(TweenableProperty.Position, new Vector3(0,100,0), 1);
    /// </code>
    /// This moves a node to (0,100,0) over 1 second.
    /// </example>
    /// By default, chained tween commands will wait until the previous command has completed.
    /// The tween command Then() is an empty command which can be used to make tweens more readable.
    /// <example>
    /// <code>
    /// Tween.Start(target).MoveTo(new Vector3(0,100,0), 3).Then().ScaleBy(new Vector3(1,1,1), 2, Bounce.EaseOut);
    /// </code>
    /// The node will move and, upon completion, scale.
    /// </example>
    /// Commands can be told to execute simultaneously by using the And() command.
    /// This command will tell a the next command to execute at the same time as the previous command.
    /// <exmaple>
    /// <code>
    /// Tween.Start(target).MoveTo(new Vector3(0,100,0), 3).And().ScaleBy(new Vector3(1,1,1), 2, Bounce.EaseOut);
    /// </code>
    /// The node will move and scale at the same time.
    /// </exmaple>
    /// </summary>

    Tween.DurationEpsilon = 0.000001;

    //private delegate T PropertyOperationFunc<T>(T a, T b);

    //private delegate T EasePropertyFunc<T>(EaseFunction ease, float t, T b, T e, float d);

    /// <summary>
    /// Delegate function which returns a property for the tween.
    /// </summary>
    /// <typeparam name="T">Type of property to return.</typeparam>
    /// <param name="target">The target object to fetch the property from.</param>
    /// <param name="propertyType">The property type being fetched (use Custom for non-enumerated properties).</param>
    /// <param name="value">The resulting value of the fetched property.</param>
    //public delegate void GetPropertyFunc<T>(ITweenable target, TweenableProperty propertyType, out T value);

    /// <summary>
    /// Delegate function which sets a property for the tween.
    /// </summary>
    /// <typeparam name="T">Type of property to set.</typeparam>
    /// <param name="target">The target object to set the property on.</param>
    /// <param name="propertyType">The property type being set (use Custom for non-enumerated properties).</param>
    /// <param name="value">The value to set into the property.</param>
    //public delegate void SetPropertyFunc<T>(ITweenable target, TweenableProperty propertyType, T value);

    /// <summary>
    /// Passing SyncDurations into the duration of a tween will synchronize it with the previous command (useful for commands that run simultaneously).
    /// </summary>
    Tween.SyncDurations = -1;

    /// <summary>
    /// Use with the NumLoops field on TweenProperties to loop infinitely.
    /// </summary>
    Tween.InfiniteLoops = -1;

    /// <summary>
    /// Contains different tween operations available for calculating the target value when performing a By() tween.
    /// </summary>
    Tween.TweenByOperation =
    {
        Default: 0,
        Add: 1,
        Multiply: 2
    };

    /// <summary>
    /// Event dispatched when the tween completes.
    /// </summary>
    //public event EventHandler<DataEventArgs<ITweenable>> Completed;

    /// <summary>
    /// Event dispatched when the tween updates.
    /// </summary>
    //public event EventHandler<DataEventArgs<ITweenable>> Updated;

    /// <summary>
    /// Event dispatched when the tween started.
    /// </summary>
    //public static event EventHandler<DataEventArgs<ITweenable>> TweenTargetAdded;
    /*
    private static void OnTweenTargetAdded(DataEventArgs<ITweenable> e)
    {
        EventHandler<DataEventArgs<ITweenable>> handler = TweenTargetAdded;
        if (handler != null) handler(null, e);
    }
    */
    /// <summary>
    /// Event dispatched when the tween removed.
    /// </summary>
    //public static event EventHandler<DataEventArgs<ITweenable>> TweenTargetRemoved;
    /*
    private static void OnTweenTargetRemoved(DataEventArgs<ITweenable> e)
    {
        EventHandler<DataEventArgs<ITweenable>> handler = TweenTargetRemoved;
        if (handler != null) handler(null, e);
    }
    */

    // object with array members
    Tween.ActiveTweens = {};

    /// <summary>
    /// Constructs a new tween on a target.
    /// </summary>
    /// <param name="target">The target node to tween.</param>
    /// <param name="props">Optional tween properties.</param>
    Tween.prototype.initTween = function (target, props)
    {
        if (Tween.ActiveTweens == null)
        {
            Tween.ActiveTweens = {};
        }

        this.Target = target;
        this.Properties = props || new Tween.TweenProperties();
        //TODO this.Target.Updated += Update;

        this.Steps = [];
        this.CurrentStep = -1;
        this.CombineNext = false;
    };

    /// <summary>
    /// Starts a new tween on a target.
    /// </summary>
    /// <param name="target">The target node to tween.</param>
    /// <param name="props">Optional tween properties.</param>
    Tween.Start = function (target, props)
    {
        var tween = new Tween(target, props);
        Tween.AddTween(target, tween);
        return tween;
    };

    /// <summary>
    /// Stops a running tween.
    /// </summary>
    Tween.prototype.Stop = function (runCompletionCallback)
    {
        runCompletionCallback = !!runCompletionCallback;

        // TODO this.Target.Updated -= Update;
        Tween.RemoveTween(this.Target, this);
        if (runCompletionCallback && this.Completed != null)
        {
            this.Completed(this, /* TODO new DataEventArgs<ITweenable>(Target) */ this.Target);
        }
    };

    /// <summary>
    /// Stops all active tweens on a target.
    /// </summary>
    /// <param name="target">Target to stop tweens on.</param>
    /// <param name="runCompletionCallbacks">If true and a completion callbacks has been set, runs the callbacks.</param>
    Tween.StopAll = function (target, runCompletionCallbacks)
    {
        if (typeof target == 'boolean') {
            return Tween.StopAllNoTarget(target);
        }
        var tweens = Tween.GetTweens(target);
        _.forEach (tweens, function (tween)
        {
            tween.Stop(runCompletionCallbacks);
        });
        if (Tween.ActiveTweens != null && Tween.ActiveTweens[target.Id.toString()])
        {
            delete Tween.ActiveTweens[target.Id.toString];
            // TODO this.OnTweenTargetRemoved(new DataEventArgs<ITweenable>(target));
        }
    };

    /// <summary>
    /// Stops all active tweens.
    /// </summary>
    /// <param name="runCompletionCallbacks">If true and a completion callbacks has been set, runs the callbacks.</param>
    Tween.StopAllNoTarget = function (runCompletionCallbacks)
    {
        if (Tween.ActiveTweens == null)
        {
            return;
        }

        _.forEach (Tween.ActiveTweens, function (tweenList)
        {
            _.forEach (tweenList, function (tween)
            {
                tween.Stop(runCompletionCallbacks);
            });
        });
        Tween.ActiveTweens = {};
    };

    /// <summary>
    /// Returns a list of active tweens on a target.
    /// </summary>
    /// <param name="target">Target to retrieve tweens on.</param>
    /// <returns>Returns a list of active tweens on the target.</returns>
    Tween.GetTweens = function (target)
    {
        if (Tween.ActiveTweens == null)
        {
            return [];
        }

        var activeTweenList = Tween.ActiveTweens[target.Id.toString()];
        if (activeTweenList == null)
        {
            return [];
        }

        return activeTweenList.slice(0);
    };

    Tween.AddTween = function (target, tween)
    {
        var activeTweenList = Tween.ActiveTweens[target.Id.toString()];
        if (activeTweenList == null)
        {
            activeTweenList = [];
            Tween.ActiveTweens[target.Id.toString()] =  activeTweenList;
            // TODO OnTweenTargetAdded(new DataEventArgs<ITweenable>(target));
        }
        activeTweenList.push(tween);
    };

    Tween.RemoveTween = function (target, tween)
    {
        var activeTweenList = Tween.ActiveTweens[target.Id.toString()];
        if (activeTweenList == null)
        {
            return;
            //throw new KeyNotFoundException("Could not find tweens for target node");
        }

        var targetIndex = activeTweenList.indexOf(tween);
        if (targetIndex != -1) {
            activeTweenList.splice(targetIndex, 1);
            if (activeTweenList.length == 0) {
                delete Tween.ActiveTweens[target.Id.toString()];
                // TODO OnTweenTargetRemoved(new DataEventArgs<ITweenable>(target));
            }
        }
    };

    Tween.RemoveAll = function() {
        if (Tween.ActiveTweens == null)
        {
            return;
        }

        _.forEach (Tween.ActiveTweens, function (tweenList)
        {
            if (tweenList) {
                for (var i = 0; i < tweenList.length; i++) {
                    var tween = tweenList[i];
                    this.RemoveTween(tween.Target, tween);
                }
            }
        }, this);
    };

    Tween.UpdateAll = function () {
        if (Tween.ActiveTweens == null)
        {
            return;
        }

        _.forEach (Tween.ActiveTweens, function (tweenList)
        {
            if (tweenList) {
                for (var i = 0; i < tweenList.length; i++) {
                    var tween = tweenList[i];
                    tweenList[i].Update(tween.Target);
                }
            }
        });
    };

    Tween.prototype.Update = function (target, args)
    {
        var sceneTarget = target;
        if (this.CurrentStep == -1)
        {
            this.NextStep();
        }
        var stepGroup = this.Steps[this.CurrentStep];
        var allStepsComplete = true;
        _.forEach (stepGroup, function (step)
        {
            if (!step.Completed) {
                var complete = this.UpdateStep(step, EngineManagers.Time);
                allStepsComplete = allStepsComplete && complete;
                if (step.Update != null) {
                    step.Update(sceneTarget, EngineManagers.Time);
                }
            }
        }, this);
        if (this.Updated != null)
        {
            this.Updated(this /*,  TODO new DataEventArgs<ITweenable>(sceneTarget) */);
        }
        if (allStepsComplete)
        {
            this.NextStep();
        }
    };

    /// <summary>
    /// Tween command that tells the following command to run at the same time as the previous command.
    /// By default, chaining commands will force commands to wait for each other.
    /// </summary>
    /// <returns>Returns the tween for chaining.</returns>
    Tween.prototype.And = function ()
    {
        this.CombineNext = true;
        return this;
    };

    /// <summary>
    /// Tween command that tells the following command to wait for the previous command to complete before running.
    /// This is just for readability, as chaining does this by default.
    /// </summary>
    /// <returns>Returns the tween for chaining.</returns>
    Tween.prototype.Then = function ()
    {
        return this;
    };
/*
    /// <summary>
    /// A tween command that tweens a property to a target value.
    /// </summary>
    /// <param name="propertyType">The property type to tween.</param>
    /// <param name="targetValue">The target value to tween to.</param>
    /// <param name="duration">The duration of the interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <returns>Returns the tween for chaining.</returns>
    Tween.prototype.To = function (propertyType, targetValue, duration, ease)
    {
        return To(SetTweenableProperty, GetTweenableProperty, EaseUtil.Ease, propertyType, targetValue, duration, ease);
    }

        public Tween To(TweenableProperty propertyType, float targetValue, float duration)
        {
            return To(propertyType, targetValue, duration, null);
        }

        /// <summary>
        /// A tween command that tweens a property to a target value.
        /// </summary>
        /// <param name="propertyType">The property type to tween.</param>
        /// <param name="targetValue">The target value to tween to.</param>
        /// <param name="duration">The duration of the interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
        /// <returns>Returns the tween for chaining.</returns>
        public Tween To(TweenableProperty propertyType, Vector3 targetValue, float duration, EaseFunction ease)
        {
            return To(SetTweenableProperty, GetTweenableProperty, EaseUtil.Ease, propertyType, targetValue, duration, ease);
        }

        public Tween To(TweenableProperty propertyType, Vector3 targetValue, float duration)
        {
            return To(propertyType, targetValue, duration, null);
        }

        /// <summary>
        /// A tween command that tweens a property to a target value.
        /// </summary>
        /// <param name="propertyType">The property type to tween.</param>
        /// <param name="targetValue">The target value to tween to.</param>
        /// <param name="duration">The duration of the interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
        /// <returns>Returns the tween for chaining.</returns>
        public Tween To(TweenableProperty propertyType, Color3 targetValue, float duration, EaseFunction ease)
        {
            return To(SetTweenableProperty, GetTweenableProperty, EaseUtil.Ease, propertyType, targetValue, duration, ease);
        }

        public Tween To(TweenableProperty propertyType, Color3 targetValue, float duration)
        {
            return To(propertyType, targetValue, duration, null);
        }
*/
        /// <summary>
        /// A tween command that tweens a property to a target value.
        /// </summary>
        /// <param name="propertyType">The property type to tween.</param>
        /// <param name="targetValue">The target value to tween to.</param>
        /// <param name="duration">The duration of the interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
        /// <returns>Returns the tween for chaining.</returns>
        Tween.prototype.To = function (propertyType, targetValue, duration, ease)
        {
            var easeFunc;
            if (targetValue instanceof PIXI.Point) {
                easeFunc = easePoint;
            }
            else {
                easeFunc = easeFloat;
            }
            return this.ToInternal(SetTweenableProperty, GetTweenableProperty, easeFunc, propertyType, targetValue, duration, ease);
        };
/*
        public Tween To(TweenableProperty propertyType, Quaternion targetValue, float duration)
        {
            return To(propertyType, targetValue, duration, null);
        }

*/
        /// <summary>
        /// A tween command that tweens a custom property to a target value.
        /// </summary>
        /// <param name="setFunc">Setter function for the target property.</param>
        /// <param name="getFunc">Getter function for the target property.</param>
        /// <param name="targetValue">The target value to tween to.</param>
        /// <param name="duration">The duration of the interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default (pass in null).</param>
        /// <returns>Returns the tween for chaining.</returns>
        Tween.prototype.CustomTo = function (setFunc, getFunc, targetValue, duration, ease)
        {
            var easeFunc = easeFloat;
            if (targetValue instanceof PIXI.Point) {
                easeFunc = easePoint;
            }
            return this.ToInternal(setFunc, getFunc, easeFunc, Tween.TweenableProperty.Custom, targetValue, duration, ease);
        };


        Tween.prototype.ToInternal = function (setFunc, getFunc, easeFunc, propertyType, targetValue, duration, ease)
        {
            ease = ease || TWEEN.Easing.Linear.None;

            var step = new Step();
            step.Duration = duration < Tween.DurationEpsilon ? Tween.DurationEpsilon : duration;

            if (Math.abs(step.Duration - Tween.SyncDurations) < Tween.DurationEpsilon)
            {
                step.Duration = this.Steps.length != 0 ? this.Steps[this.Steps.length - 1].length != 0 ? this.Steps[this.Steps.length - 1][this.Steps[this.Steps.length - 1].length - 1].Duration : Tween.DurationEpsilon : Tween.DurationEpsilon;
            }
            step.Init = function (target)
                {
                    this.start = getFunc(target, propertyType);
                    this.end = targetValue;
                };

            step.Update = function (target, time) {
                setFunc(target, propertyType, easeFunc(ease, this.Elapsed, this.start, this.end, this.Duration));
            };

            this.AddStep(step);
            return this;
        };

        /// <summary>
        /// A tween command that tweens a property by a target value.
        /// </summary>
        /// <param name="propertyType">The property type to tween.</param>
        /// <param name="targetValue">The target value to tween by.</param>
        /// <param name="duration">The duration of the interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
        /// <param name="op">Operation to use to get target value. Default will use the default operation for a property type.</param>
        /// <returns>Returns the tween for chaining.</returns>
    Tween.prototype.By = function (propertyType, targetValue, duration, ease, op)
        {
            var easeFunc = easeFloat;
            if (targetValue instanceof PIXI.Point) {
                easeFunc = easePoint;
            }

            op = op == Tween.TweenByOperation.Default ? this.GetDefaultTweenByOperationForProperty(propertyType) : op;
            var opFunc = null;
            switch (op)
            {
                case Tween.TweenByOperation.Add:
                    opFunc = function (a, b) { return a + b; };
                    break;
                case Tween.TweenByOperation.Multiply:
                    opFunc = function (a, b) { return a*b; };
                    break;
            }
            return this.ByInternal(SetTweenableProperty, GetTweenableProperty, easeFunc, opFunc, propertyType, targetValue, duration, ease);
        }
/*
        public Tween By(TweenableProperty propertyType, float targetValue, float duration, EaseFunction ease)
        {
            return By(propertyType, targetValue, duration, ease, TweenByOperation.Default);
        }

        public Tween By(TweenableProperty propertyType, float targetValue, float duration)
        {
            return By(propertyType, targetValue, duration, null, TweenByOperation.Default);
        }

        /// <summary>
        /// A tween command that tweens a property by a target value.
        /// </summary>
        /// <param name="propertyType">The property type to tween.</param>
        /// <param name="targetValue">The target value to tween by.</param>
        /// <param name="duration">The duration of the interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
        /// <param name="op">Operation to use to get target value. Default will use the default operation for a property type.</param>
        /// <returns>Returns the tween for chaining.</returns>
        public Tween By(TweenableProperty propertyType, Vector3 targetValue, float duration, EaseFunction ease, TweenByOperation op)
        {
            op = op == TweenByOperation.Default ? GetDefaultTweenByOperationForProperty(propertyType) : op;
            PropertyOperationFunc<Vector3> opFunc = null;
            switch (op)
            {
                case TweenByOperation.Add:
                    opFunc = Vector3.Add;
                    break;
                case TweenByOperation.Multiply:
                    opFunc = Vector3.Modulate;
                    break;
            }
            return By(SetTweenableProperty, GetTweenableProperty, EaseUtil.Ease, opFunc, propertyType, targetValue, duration, ease);
        }

        public Tween By(TweenableProperty propertyType, Vector3 targetValue, float duration, EaseFunction ease)
        {
            return By(propertyType, targetValue, duration, ease, TweenByOperation.Default);
        }

        public Tween By(TweenableProperty propertyType, Vector3 targetValue, float duration)
        {
            return By(propertyType, targetValue, duration, null, TweenByOperation.Default);
        }

        /// <summary>
        /// A tween command that tweens a property by a target value.
        /// </summary>
        /// <param name="propertyType">The property type to tween.</param>
        /// <param name="targetValue">The target value to tween by.</param>
        /// <param name="duration">The duration of the interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
        /// <param name="op">Operation to use to get target value. Default will use the default operation for a property type.</param>
        /// <returns>Returns the tween for chaining.</returns>
        public Tween By(TweenableProperty propertyType, Color3 targetValue, float duration, EaseFunction ease, TweenByOperation op)
        {
            op = op == TweenByOperation.Default ? GetDefaultTweenByOperationForProperty(propertyType) : op;
            PropertyOperationFunc<Color3> opFunc = null;
            switch (op)
            {
                case TweenByOperation.Add:
                    opFunc = Color3.Add;
                    break;
                case TweenByOperation.Multiply:
                    opFunc = Color3.Modulate;
                    break;
            }
            return By(SetTweenableProperty, GetTweenableProperty, EaseUtil.Ease, opFunc, propertyType, targetValue, duration, ease);
        }

        public Tween By(TweenableProperty propertyType, Color3 targetValue, float duration, EaseFunction ease)
        {
            return By(propertyType, targetValue, duration, ease, TweenByOperation.Default);
        }

        public Tween By(TweenableProperty propertyType, Color3 targetValue, float duration)
        {
            return By(propertyType, targetValue, duration, null, TweenByOperation.Default);
        }

        /// <summary>
        /// A tween command that tweens a property by a target value.
        /// </summary>
        /// <param name="propertyType">The property type to tween.</param>
        /// <param name="targetValue">The target value to tween by.</param>
        /// <param name="duration">The duration of the interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
        /// <param name="op">Operation to use to get target value. Default will use the default operation for a property type.</param>
        /// <returns>Returns the tween for chaining.</returns>
        public Tween By(TweenableProperty propertyType, Quaternion targetValue, float duration, EaseFunction ease, TweenByOperation op)
        {
            op = op == TweenByOperation.Default ? GetDefaultTweenByOperationForProperty(propertyType) : op;
            PropertyOperationFunc<Quaternion> opFunc = null;
            switch (op)
            {
                case TweenByOperation.Add:
                    opFunc = (a, b) => { return a*b; };
                    break;
                case TweenByOperation.Multiply:
                    opFunc = (a, b) => { return a*b; };
                    break;
            }
            return By(SetTweenableProperty, GetTweenableProperty, EaseUtil.Ease, opFunc, propertyType, targetValue, duration, ease);
        }

        public Tween By(TweenableProperty propertyType, Quaternion targetValue, float duration, EaseFunction ease)
        {
            return By(propertyType, targetValue, duration, ease, TweenByOperation.Default);
        }

        public Tween By(TweenableProperty propertyType, Quaternion targetValue, float duration)
        {
            return By(propertyType, targetValue, duration, null, TweenByOperation.Default);
        }

*/
    Tween.prototype.ByInternal = function (setFunc, getFunc,  easeFunc, addFunc, propertyType, targetValue, duration, ease)
        {
            ease = ease || TWEEN.Easing.Linear.None;

            var step = new Step();
            step.Duration = duration < Tween.DurationEpsilon ? Tween.DurationEpsilon : duration

            if (Math.abs(step.Duration - Tween.SyncDurations) < Tween.DurationEpsilon)
            {
                step.Duration = this.Steps.length != 0 ? this.Steps[this.Steps.length - 1].length != 0 ? this.Steps[this.Steps.length - 1][this.Steps[this.Steps.length - 1].length - 1].Duration : Tween.DurationEpsilon : Tween.DurationEpsilon;
            }
            step.Init = function (target)
            {
                this.start = getFunc(target, propertyType);
                this.end = addFunc(this.start, targetValue);
            }
            step.Update = function (target, time) {
                setFunc(target, propertyType, easeFunc(ease, this.Elapsed, this.start, this.end, this.Duration));
            }
            this.AddStep(step);
            return this;
        };

        /// <summary>
        /// Calls a delegate function with no parameters.
        /// </summary>
        /// <param name="callback">The callback to call.</param>
        /// <returns>Returns the tween for chaining.</returns>
        Tween.prototype.Call = function (callback)
        {
            var step = new Step();
            step.Update = function (target, time) { callback(target) };
            this.AddStep(step);
            return this;
        };

        /// <summary>
        /// Calls a delegate function with the node as the parameter.
        /// </summary>
        /// <param name="callback">The callback to call.</param>
        /// <returns>Returns the tween for chaining.</returns>
        Tween.prototype.CallTween = function (callback)
        {
            var step = new Step();
            step.Update = function (target, time) { callback(target) };
            this.AddStep(step);
            return this;
        };

        /// <summary>
        /// A tween command that waits for a specified amount of time in seconds.
        /// </summary>
        /// <param name="time">The amount of time to wait in seconds.</param>
        /// <returns>Returns the tween for chaining.</returns>
        Tween.prototype.Wait = function (time)
        {
            var step = new Step();
            step.Duration = time < Tween.DurationEpsilon ? Tween.DurationEpsilon : time;
            this.AddStep(step);
            return this;
        };

        /// <summary>
        /// Moves the node to a target position.
        /// </summary>
        /// <param name="targetValue">The target position to move to.</param>
        /// <param name="duration">The duration of interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
        /// <returns>Returns the tween for chaining.</returns>
        Tween.prototype.MoveTo = function (targetValue, duration, ease)
        {
            return this.To(Tween.TweenableProperty.Position, targetValue, duration, ease);
        };
    /*
        public Tween MoveTo(Vector3 targetValue, float duration)
        {
            return MoveTo(targetValue, duration, null);
        }

        /// <summary>
        /// Moves the node by an amount.
        /// </summary>
        /// <param name="targetValue">The amount to move by.</param>
        /// <param name="duration">The duration of interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
        /// <param name="op">Operation to use to get target value. Default will use the default operation for a property type.</param>
        /// <returns>Returns the tween for chaining.</returns>
        public Tween MoveBy(Vector3 targetValue, float duration, EaseFunction ease, TweenByOperation op)
        {
            return By(TweenableProperty.Position, targetValue, duration, ease);
        }

        public Tween MoveBy(Vector3 targetValue, float duration, EaseFunction ease)
        {
            return MoveBy(targetValue, duration, ease, TweenByOperation.Default);
        }

        public Tween MoveBy(Vector3 targetValue, float duration)
        {
            return MoveBy(targetValue, duration, null, TweenByOperation.Default);
        }
     */
        /// <summary>
        /// Moves the node using a bezier path.
        /// The bezier path should be specified relative to the node's current position.
        /// </summary>
        /// <param name="path">The bezier path to mode the node by.</param>
        /// <param name="duration">The duration of interpolation.</param>
        /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
        /// <returns>Returns the tween for chaining.</returns>
        Tween.prototype.MoveByBezier = function (path, duration, ease)
        {

            var time = 0;
            var lastPath = new Vector2(0, 0);
            return this.CustomTo(
                function (target, propertyType, value)
                {
                    time = value;
                    var pos = path.PositionAt(time);
                    target.X += pos.X - lastPath.X;
                    target.Y += pos.Y - lastPath.Y;
                    lastPath.X = pos.X;
                    lastPath.Y = pos.Y;
                },
                function (target, propertyType, value)
                {
                    value = time;
                    return value;
                },
                1,
                duration,
                ease);
        };
/*
    public Tween MoveByBezier(Bezier2D path, float duration)
    {
        return MoveByBezier(path, duration, null);
    }
*/
    /// <summary>
    /// Scales the node to a target value.
    /// </summary>
    /// <param name="targetValue">The target value to scale to.</param>
    /// <param name="duration">The duration of interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <returns>Returns the tween for chaining.</returns>
    Tween.prototype.ScaleTo = function (targetValue, duration, ease)
    {
        return this.To(Tween.TweenableProperty.Scale, targetValue, duration, ease);
    };
/*
    public Tween ScaleTo(Vector3 targetValue, float duration)
    {
        return ScaleTo(targetValue, duration, null);
    }

    /// <summary>
    /// Scales the node by a value.
    /// </summary>
    /// <param name="targetValue">The value to scale by.</param>
    /// <param name="duration">The duration of interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <param name="op">Operation to use to get target value. Default will use the default operation for a property type.</param>
    /// <returns>Returns the tween for chaining.</returns>
    public Tween ScaleBy(Vector3 targetValue, float duration, EaseFunction ease, TweenByOperation op)
    {
        return By(TweenableProperty.Scale, targetValue, duration, ease);
    }

    public Tween ScaleBy(Vector3 targetValue, float duration, EaseFunction ease)
    {
        return ScaleBy(targetValue, duration, ease, TweenByOperation.Default);
    }

    public Tween ScaleBy(Vector3 targetValue, float duration)
    {
        return ScaleBy(targetValue, duration, null, TweenByOperation.Default);
    }
*/
    /// <summary>
    /// Fades the node to a target value.
    /// </summary>
    /// <param name="targetValue">The target value to fade to.</param>
    /// <param name="duration">The duration of interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <returns>Returns the tween for chaining.</returns>
    Tween.prototype.FadeTo = function (targetValue, duration, ease)
    {
        return this.To(Tween.TweenableProperty.Alpha, targetValue, duration, ease);
    };
/*
    public Tween FadeTo(float targetValue, float duration)
    {
        return FadeTo(targetValue, duration, null);
    }

    /// <summary>
    /// Fades the node by a value.
    /// </summary>
    /// <param name="targetValue">The value to fade by.</param>
    /// <param name="duration">The duration of interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <param name="op">Operation to use to get target value. Default will use the default operation for a property type.</param>
    /// <returns>Returns the tween for chaining.</returns>
    public Tween FadeBy(float targetValue, float duration, EaseFunction ease, TweenByOperation op)
    {
        return By(TweenableProperty.Alpha, targetValue, duration, ease);
    }

    public Tween FadeBy(float targetValue, float duration, EaseFunction ease)
    {
        return FadeBy(targetValue, duration, ease, TweenByOperation.Default);
    }

    public Tween FadeBy(float targetValue, float duration)
    {
        return FadeBy(targetValue, duration, null, TweenByOperation.Default);
    }
    */
    /// <summary>
    /// Rotates the node to target euler angles.
    /// </summary>
    /// <param name="targetValue">The euler angles to rotate to.</param>
    /// <param name="duration">The duration of interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <returns>Returns the tween for chaining.</returns>
    Tween.prototype.RotateTo = function (targetValue, duration, ease)
    {
        ease = ease || null;
        return this.To(Tween.TweenableProperty.Rotation, targetValue, duration, ease);
    };

    /// <summary>
    /// Rotates the node by euler angles.
    /// </summary>
    /// <param name="targetValue">The euler angles to rotate by.</param>
    /// <param name="duration">The duration of interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <param name="op">Operation to use to get target value. Default will use the default operation for a property type.</param>
    /// <returns>Returns the tween for chaining.</returns>
    Tween.prototype.RotateBy = function (targetValue, duration, ease, op)
    {
        ease = ease || null;
        op = op || Tween.TweenByOperation.Default;

        return this.By(Tween.TweenableProperty.Rotation, targetValue, duration, ease, op);
    };
    /*
    Tween.prototype.RotateBy = function (targetValue, duration, ease)
    {
        return this.RotateBy(targetValue, duration, ease, Tween.TweenByOperation.Default);
    }

    Tween.prototype.RotateBy = function (targetValue, duration)
    {
        return this.RotateBy(targetValue, duration, null, Tween.TweenByOperation.Default);
    };

    /// <summary>
    /// Rotates the node to a target quaternion.
    /// </summary>
    /// <param name="targetValue">The quaternion to rotate to.</param>
    /// <param name="duration">The duration of interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <returns>Returns the tween for chaining.</returns>
    public Tween RotateTo(Quaternion targetValue, float duration, EaseFunction ease)
    {
        return To(TweenableProperty.Rotation, targetValue, duration, ease);
    }

    public Tween RotateTo(Quaternion targetValue, float duration)
    {
        return RotateTo(targetValue, duration, null);
    }

    /// <summary>
    /// Rotates the node by a quaternion.
    /// </summary>
    /// <param name="targetValue">The quaternion to rotate by.</param>
    /// <param name="duration">The duration of interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <param name="op">Operation to use to get target value. Default will use the default operation for a property type.</param>
    /// <returns>Returns the tween for chaining.</returns>
    public Tween RotateBy(Quaternion targetValue, float duration, EaseFunction ease, TweenByOperation op)
    {
        return By(TweenableProperty.Rotation, targetValue, duration, ease);
    }

    public Tween RotateBy(Quaternion targetValue, float duration, EaseFunction ease)
    {
        return RotateBy(targetValue, duration, ease, TweenByOperation.Default);
    }

    public Tween RotateBy(Quaternion targetValue, float duration)
    {
        return RotateBy(targetValue, duration, null, TweenByOperation.Default);
    }

    /// <summary>
    /// Tweens the node's color to a target color.
    /// </summary>
    /// <param name="targetValue">The target color.</param>
    /// <param name="duration">The duration of interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <returns>Returns the tween for chaining.</returns>
    public Tween ColorTo(Color3 targetValue, float duration, EaseFunction ease)
    {
        return To(TweenableProperty.Color, targetValue, duration, ease);
    }

    public Tween ColorTo(Color3 targetValue, float duration)
    {
        return ColorTo(targetValue, duration, null);
    }

    /// <summary>
    /// Tweens the node's color by a target color.
    /// </summary>
    /// <param name="targetValue">The color to modulate by.</param>
    /// <param name="duration">The duration of interpolation.</param>
    /// <param name="ease">The ease function to use while interpolating. Linear.EaseNone is the default.</param>
    /// <param name="op">Operation to use to get target value. Default will use the default operation for a property type.</param>
    /// <returns>Returns the tween for chaining.</returns>
    public Tween ColorBy(Color3 targetValue, float duration, EaseFunction ease, TweenByOperation op)
    {
        return By(TweenableProperty.Color, targetValue, duration, ease);
    }

    public Tween ColorBy(Color3 targetValue, float duration, EaseFunction ease)
    {
        return ColorBy(targetValue, duration, ease, TweenByOperation.Default);
    }

    public Tween ColorBy(Color3 targetValue, float duration)
    {
        return ColorBy(targetValue, duration, null, TweenByOperation.Default);
    }

        #endregion

        #region Property Access
*/
    var SetTweenableProperty = function (target, propertyType, value)
    {
        switch (propertyType)
        {
            case Tween.TweenableProperty.Alpha:
                target.Alpha = value;
                break;
            case Tween.TweenableProperty.Position_X:
                target.X = value;
                break;
            case Tween.TweenableProperty.Position_Y:
                target.Y = value;
                break;
            case Tween.TweenableProperty.Position_Z:
                target.Z = value;
                break;
            case Tween.TweenableProperty.Scale_X:
                target.ScaleX = value;
                break;
            case Tween.TweenableProperty.Scale_Y:
                target.ScaleY = value;
                break;
            case Tween.TweenableProperty.Scale_Z:
                target.ScaleZ = value;
                break;
            case Tween.TweenableProperty.Rotation_X:
                target.RotationX = value;
                break;
            case Tween.TweenableProperty.Rotation_Y:
                target.RotationY = value;
                break;
            case Tween.TweenableProperty.Rotation_Z:
                target.RotationZ = value;
                break;
            case Tween.TweenableProperty.Color_R:
                // TODO
                break;
            case Tween.TweenableProperty.Color_G:
                // TODO
                break;
            case Tween.TweenableProperty.Color_B:
                // TODO
                break;
            case Tween.TweenableProperty.Position:
                target.SetPosition(value);
                break;
            case Tween.TweenableProperty.Scale:
                target.SetScale(value);
                break;
            case Tween.TweenableProperty.Rotation:
                target.SetRotation(value);
                break;
            default:
                throw "Property Type " + propertyType.ToString() + " is not implemented or property is not a float";
        }
    };
/*
    private static void SetTweenableProperty(ITweenable target, TweenableProperty propertyType, Vector3 value)
    {
        switch (propertyType)
        {
            case TweenableProperty.Position:
                target.SetPosition(value);
                break;
            case TweenableProperty.Scale:
                target.SetScale(value);
                break;
            case TweenableProperty.Rotation:
                target.SetRotation(value);
                break;
            default:
                throw new Exception("Property Type " + propertyType.ToString() + " is not implemented or property is not a Vector3");
        }
    }

    private static void SetTweenableProperty(ITweenable target, TweenableProperty propertyType, Color3 value)
    {
        switch (propertyType)
        {
            case TweenableProperty.Color:
                ((IBaseMaterial) target.Material).DiffuseColor = value;
                break;
            default:
                throw new Exception("Property Type " + propertyType.ToString() + " is not implemented or property is not a Color3");
        }
    }

    private static void SetTweenableProperty(ITweenable target, TweenableProperty propertyType, Quaternion value)
    {
        switch (propertyType)
        {
            case TweenableProperty.Rotation:
                target.SetRotation(value);
                break;
            default:
                throw new Exception("Property Type " + propertyType.ToString() + " is not implemented or property is not a Quaternion");
        }
    }
*/
    var GetTweenableProperty = function (target, propertyType)
    {
        var value = 0;
        switch (propertyType)
        {
            case Tween.TweenableProperty.Alpha:
                value = target.Alpha;
                break;
            case Tween.TweenableProperty.Position_X:
                value = target.X;
                break;
            case Tween.TweenableProperty.Position_Y:
                value = target.Y;
                break;
            case Tween.TweenableProperty.Position_Z:
                value = target.Z;
                break;
            case Tween.TweenableProperty.Scale_X:
                value = target.ScaleX;
                break;
            case Tween.TweenableProperty.Scale_Y:
                value = target.ScaleY;
                break;
            case Tween.TweenableProperty.Scale_Z:
                value = target.ScaleZ;
                break;
            case Tween.TweenableProperty.Rotation_X:
                value = target.RotationX;
                break;
            case Tween.TweenableProperty.Rotation_Y:
                value = target.RotationY;
                break;
            case Tween.TweenableProperty.Rotation_Z:
                value = target.RotationZ;
                break;
            case Tween.TweenableProperty.Color_R:
                value = 0; // TODO ((IBaseMaterial) target.Material).DiffuseColor.Red;
                break;
            case Tween.TweenableProperty.Color_G:
                value = 0; // TODO ((IBaseMaterial) target.Material).DiffuseColor.Green;
                break;
            case Tween.TweenableProperty.Color_B:
                value = 0; // TODO ((IBaseMaterial) target.Material).DiffuseColor.Blue;
                break;
            case Tween.TweenableProperty.Position:
                value = target.Position;
                break;
            case Tween.TweenableProperty.Scale:
                value = target.Scale;
                break;
            case Tween.TweenableProperty.Rotation:
                value = target.Rotation;
                break;
            default:
                throw "Property Type " + propertyType.ToString() + " is not implemented or property is not a float";
        }

        return value;
    };
/*
    private void GetTweenableProperty(ITweenable target, TweenableProperty propertyType, out Vector3 value)
    {
        switch (propertyType)
        {
            case TweenableProperty.Position:
                value = target.Position;
                break;
            case TweenableProperty.Scale:
                value = target.Scale;
                break;
            case TweenableProperty.Rotation:
                value = target.EulerRotation;
                break;
            default:
                throw new Exception("Property Type " + propertyType.ToString() + " is not implemented or property is not a Vector3");
        }
    }

    private void GetTweenableProperty(ITweenable target, TweenableProperty propertyType, out Color3 value)
    {
        switch (propertyType)
        {
            case TweenableProperty.Color:
                value = ((IBaseMaterial) target.Material).DiffuseColor;
                break;
            default:
                throw new Exception("Property Type " + propertyType.ToString() + " is not implemented or property is not a Color3");
        }
    }

    private void GetTweenableProperty(ITweenable target, TweenableProperty propertyType, out Quaternion value)
    {
        switch (propertyType)
        {
            case TweenableProperty.Rotation:
                value = target.Rotation;
                break;
            default:
                throw new Exception("Property Type " + propertyType.ToString() + " is not implemented or property is not a Quaternion");
        }
    }
 */
    Tween.prototype.GetDefaultTweenByOperationForProperty = function (propertyType)
    {
        switch (propertyType)
        {
            case Tween.TweenableProperty.Alpha:
            case Tween.TweenableProperty.Color:
            case Tween.TweenableProperty.Color_B:
            case Tween.TweenableProperty.Color_G:
            case Tween.TweenableProperty.Color_R:
            case Tween.TweenableProperty.Position:
            case Tween.TweenableProperty.Position_X:
            case Tween.TweenableProperty.Position_Y:
            case Tween.TweenableProperty.Position_Z:
            case Tween.TweenableProperty.Rotation:
            case Tween.TweenableProperty.Rotation_X:
            case Tween.TweenableProperty.Rotation_Y:
            case Tween.TweenableProperty.Rotation_Z:
                return Tween.TweenByOperation.Add;
            case Tween.TweenableProperty.Scale:
            case Tween.TweenableProperty.Scale_X:
            case Tween.TweenableProperty.Scale_Y:
            case Tween.TweenableProperty.Scale_Z:
                return Tween.TweenByOperation.Multiply;
        }
        throw ("No default operation for property: " + propertyType);
    }



    Tween.prototype.NextStep = function ()
    {
        var stepsComplete = ++this.CurrentStep >= this.Steps.length;
        var loop = false;
        if (stepsComplete)
        {
            loop = this.Properties.NumLoops == -1 || this.Properties.NumLoops-- != 0;
            this.CurrentStep = loop ? 0 : this.CurrentStep;
        }
        if (stepsComplete && !loop)
        {
            this.Stop(true);
        }
        else
        {
            var stepGroup = this.Steps[this.CurrentStep];
            _.forEach (stepGroup, function (step)
            {
                step.Clear();
                if (step.Init != null)
                {
                    step.Init(this.Target);
                }
            }, this);
        }
    };

    Tween.prototype.UpdateStep = function (step, time)
    {
        var dT = this.Properties.FrameBased ? 1 : time.ElapsedGameTime.TotalSeconds;
        if (step.Elapsed + dT >= step.Duration)
        {
            dT = step.Duration - step.Elapsed;
            step.Completed = true;
        }
        step.Elapsed += dT;
        return step.Completed;
    };

    Tween.prototype.AddStep = function (step)
    {
        //Add a new step group to the end of the list
        if (!this.CombineNext || this.Steps.length == 0)
        {
            this.Steps.push([step]);
        }
        //Combine with the last step group
        else
        {
            this.CombineNext = false;
            this.Steps[this.Steps.length - 1].push(step);
        }
    };




    var Step = function () {
        this.initStep();
    };

    Step.prototype.initStep = function () {
        this.Init = null;
        this.Update = null;
        this.Elapsed = 0;
        this.Duration = 0;
        this.Completed = false;
        this.start = null;
        this.end = null;
    };

    Step.prototype.Clear = function () {
        this.Elapsed = 0;
        this.Completed = false;
    };

    return Tween;
});


